import { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, OnChangeFn, PaginationState, useReactTable } from '@tanstack/react-table';

import {
  adjustMinimalClassTimeAcrossTZ,
  GetCoachAttendanceRollupResult,
  StudentGroupAuditWithClassTimeLevel,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatStudentGrades } from '@/lib/student-group-utils.js';
import { CoachAttendanceAbsences } from './coach-attendance-absences.js';

import { EntityTable } from '@/components/table/entity-table.js';
import { InfoCard } from '@/components/info-card.js';
import { Badge } from '@/components/ui/badge.js';
import { PeopleBadgeList } from '../../scheduling/student-groups/-components/people-badge-list.js';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting.js';
import { CountAsReassignedToggle } from './toggle-count-as-reassigned.js';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  EmptyArray,
  getCoachAttendanceRollupsForGroups,
  getCoachOneTimeStudentGroups,
  getCoachReassignedStudentGroups,
} from '@/lib/api-calls.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { Check } from 'lucide-react';

const columnHelper = createColumnHelper<StudentGroupAuditWithClassTimeLevel>();
const getColumns = (attendanceMap: Record<string, GetCoachAttendanceRollupResult>) => [
  columnHelper.accessor((row) => row.studentGroup.classTime, {
    id: 'classTimes',
    header: 'Class times (CT)',
    enableSorting: false,
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(
              classTime,
              classTime.schedule.school.org.timeZone,
              FrequentTimeZones.UsCentral,
            ),
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor('coachAssignedAtPlainDate', {
    id: 'startDate',
    header: 'Start date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor('coachUnassignedAtPlainDate', {
    id: 'endDate',
    header: 'End date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime.schedule.school.org.videoConferenceType, {
    id: 'meetingType',
    header: 'Meeting type',
    enableSorting: false,
    cell: ({ getValue }) => VideoConferenceTypeLabels[getValue()],
  }),
  columnHelper.accessor((row) => row.studentGroup.students, {
    id: 'students',
    header: 'Students',
    enableSorting: false,
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.grades, {
    id: 'grades',
    header: 'Grades',
    enableSorting: false,
    cell: ({ getValue }) => formatStudentGrades(getValue()),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.name ?? '', {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.org?.name ?? '', {
    id: 'district',
    header: 'District',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row, {
    id: 'reassigned',
    header: 'Count as reassigned',
    enableSorting: false,
    cell: ({ getValue }) => <CountAsReassignedToggle studentGroupAudit={getValue()} />,
  }),
  columnHelper.accessor('droppedByTeacher', {
    id: 'droppedByTeacher',
    header: 'Dropped by teacher',
    enableSorting: false,
    cell: ({ getValue }) => (getValue() ? <Check /> : null),
  }),
  columnHelper.accessor((row) => row.studentGroup, {
    id: 'absences',
    header: 'Teacher absences',
    enableSorting: false,
    cell: ({ getValue }) => {
      const { id, maxPlannedAbsences, shortNoticeSubRequestLimit } = getValue();
      const coachAttendanceRollup = attendanceMap[id];
      return (
        <CoachAttendanceAbsences
          coachAttendanceRollup={coachAttendanceRollup}
          maxPlannedAbsences={maxPlannedAbsences}
          shortNoticeSubRequestLimit={shortNoticeSubRequestLimit}
        />
      );
    },
  }),
];

export interface StudentGroupsCardPagination {
  pageIndex: number;
  pageSize: number;
  onPaginationChange: OnChangeFn<PaginationState>;
}

const EmptyAttendanceMap: Record<string, GetCoachAttendanceRollupResult> = {};

export const REGULAR_GROUP_TABLE_MAX_HEIGHT_CLASS = 'max-h-[400px]';

export function ReassignedStudentGroupsCard({
  coachId,
  pagination,
}: {
  coachId: string;
  pagination: StudentGroupsCardPagination;
}) {
  return (
    <StudentGroupAuditsCard
      title="Past regular student groups"
      queryOptions={getCoachReassignedStudentGroups(coachId, {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      })}
      coachId={coachId}
      pagination={pagination}
      showDroppedByTeacher
      scrollContainerClassName={REGULAR_GROUP_TABLE_MAX_HEIGHT_CLASS}
      data-testid="teachers-schedule-table-past-regular-student-groups"
    />
  );
}

export function OneTimeStudentGroupsCard({
  coachId,
  pagination,
}: {
  coachId: string;
  pagination: StudentGroupsCardPagination;
}) {
  return (
    <StudentGroupAuditsCard
      title="One-time student groups"
      queryOptions={getCoachOneTimeStudentGroups(coachId, {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      })}
      coachId={coachId}
      pagination={pagination}
      showDroppedByTeacher={false}
      data-testid="teachers-schedule-table-one-time-student-groups"
    />
  );
}

function StudentGroupAuditsCard({
  title,
  queryOptions,
  coachId,
  pagination: { pageIndex, pageSize, onPaginationChange },
  showDroppedByTeacher,
  scrollContainerClassName,
  'data-testid': dataTestId,
}: {
  title: string;
  queryOptions: ReturnType<typeof getCoachReassignedStudentGroups>;
  coachId: string;
  pagination: StudentGroupsCardPagination;
  showDroppedByTeacher: boolean;
  scrollContainerClassName?: string;
  'data-testid'?: string;
}) {
  const { data: response, error, refetch, isPending } = useQuery(queryOptions);
  const reassignedStudentGroups = useMemo(() => response?.reassignedStudentGroups || EmptyArray, [response]);
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  const studentGroupIds = reassignedStudentGroups.map((c) => c.studentGroupId);
  const { data: attendanceMap = EmptyAttendanceMap } = useQuery({
    ...getCoachAttendanceRollupsForGroups({ coachId, studentGroupIds }),
    enabled: studentGroupIds.length > 0,
    placeholderData: keepPreviousData,
    throwOnError: true,
  });

  const columns = useMemo(() => getColumns(attendanceMap), [attendanceMap]);

  const table = useReactTable({
    data: reassignedStudentGroups,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      columnVisibility: showDroppedByTeacher ? { meetingType: false } : { reassigned: false, droppedByTeacher: false },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    rowCount: totalRowCount,
  });

  const droppedByTeacherCount = reassignedStudentGroups.filter((c) => c.droppedByTeacher).length;

  return (
    <InfoCard
      headerText={
        <>
          {title}
          {showDroppedByTeacher && (
            <>
              {' '}
              <span className="text-sm font-normal text-gray-500 ml-2">
                (Dropped by teacher: {droppedByTeacherCount})
              </span>
            </>
          )}
        </>
      }
    >
      <EntityTable
        table={table}
        hasError={!!error}
        showPagination
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        totalRowCount={totalRowCount}
        emptyStateClassName="translate-y-0"
        scrollContainerClassName={scrollContainerClassName}
        data-testid={dataTestId}
      />
    </InfoCard>
  );
}
